import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSortableTable } from "../../../../../hooks/sorting/useSortableTable";
import { useColumnResize, ResizeHandle } from "../../../../../components/common/ResizeColumns";
import PoolRow, { ColHeader, SkeletonRow, POOL_DEFAULT_WIDTHS } from "./PoolRow";

const EmptyState = ({ t }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "60px 20px",
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "12px",
        color: t.text3,
        letterSpacing: "0.08em",
      }}
    >
      No associates in pool
    </Typography>
  </Box>
);

const PoolTable = ({ rows, loading, onView, onRequest }) => {
  const theme = useTheme();
  const t = theme.custom;

  const { buildGrid, getHandleProps } = useColumnResize(POOL_DEFAULT_WIDTHS);

  const { sortedRows, sortKey, sortDir, handleSort } = useSortableTable(rows ?? [], {
    defaultKey: "fullName",
    defaultDir: "asc",
    comparators: {
      freeFte:    (a, b) => (a.freeFte    ?? 0) - (b.freeFte    ?? 0),
      daysInPool: (a, b) => (a.daysInPool ?? 0) - (b.daysInPool ?? 0),
      levelCode:  (a, b) => String(a.levelCode ?? "").localeCompare(String(b.levelCode ?? "")),
    },
  });

  // Returns the correct indicator for a given column key
  const indicator = (key) => {
    if (sortKey !== key) return " ⇅";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  return (
    <Box sx={{ flex: 1, overflow: "auto" }}>
      {/* Column headers — sticky, uses EXACT same grid as PoolRow */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: buildGrid(),
          padding: "10px 20px",
          borderBottom: `1px solid ${t.border2}`,
          position: "sticky",
          top: 0,
          background: t.surface,
          zIndex: 1,
          gap: "8px",
        }}
      >
        {/* avatar spacer */}
        <Box sx={{ position: "relative" }}>
          <ResizeHandle handleProps={getHandleProps(0)} />
        </Box>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <ColHeader
            label={`Name${indicator("fullName")}`}
            sortable t={t}
            onClick={() => handleSort("fullName")}
            active={sortKey === "fullName"}
          />
          <ResizeHandle handleProps={getHandleProps(1)} />
        </Box>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <ColHeader
            label={`Level${indicator("levelCode")}`}
            sortable t={t}
            onClick={() => handleSort("levelCode")}
            active={sortKey === "levelCode"}
          />
          <ResizeHandle handleProps={getHandleProps(2)} />
        </Box>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <ColHeader label="Skills" t={t} />
          <ResizeHandle handleProps={getHandleProps(3)} />
        </Box>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <ColHeader
            label={`Free Capacity${indicator("freeFte")}`}
            sortable t={t}
            onClick={() => handleSort("freeFte")}
            active={sortKey === "freeFte"}
          />
          <ResizeHandle handleProps={getHandleProps(4)} />
        </Box>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <ColHeader label="Active Allocations" t={t} />
          <ResizeHandle handleProps={getHandleProps(5)} />
        </Box>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <ColHeader label="Pending Requests" t={t} />
          <ResizeHandle handleProps={getHandleProps(6)} />
        </Box>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <ColHeader
            label={`Days in Pool${indicator("daysInPool")}`}
            sortable t={t}
            onClick={() => handleSort("daysInPool")}
            active={sortKey === "daysInPool"}
          />
          <ResizeHandle handleProps={getHandleProps(7)} />
        </Box>
      </Box>

      {/* Skeleton rows while loading */}
      {loading && [1, 2, 3, 4].map((i) => <SkeletonRow key={i} t={t} cols={buildGrid()} />)}

      {/* Empty state */}
      {!loading && sortedRows.length === 0 && <EmptyState t={t} />}

      {/* Data rows */}
      {!loading &&
        sortedRows.map((associate, i) => (
          <PoolRow
            key={associate.associateId}
            associate={associate}
            isLast={i === sortedRows.length - 1}
            onView={onView}
            onRequest={onRequest}
            gridCols={buildGrid()}
          />
        ))}
    </Box>
  );
};

export default PoolTable;